import { ref,provide } from 'vue';
import { VhostInfo,VhostSettings  } from '@/types/Vhost'
import axios from 'axios';
import { useCookies } from '@vueuse/integrations/useCookies';
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'vue-sonner'
interface MediaStats {
  Image: number;
  Video: number;
  Audio: number;
  Text: number;
  Total: number;
}

interface Result {
  FileCounts: MediaStats;
  Views: MediaStats;
  Sends: MediaStats;
  Comments: MediaStats;
  Votes: string; // or number if you prefer to convert it
  Transfers: string; // or number if you prefer to convert it
}

interface ApiResponse {
  status: boolean;
  result: Result;
}

export default function vhost() {

	const cookies = useCookies(['vid']);
	const vhostInfo = ref<VhostInfo | undefined>(undefined);
	const totals = ref<any>(null); // Adjust type as needed
	const summary = ref<any>(null); // Adjust type as needed
	const userSummary = ref<any>(null); // Adjust type as needed
	const loading = ref(false);
	const error = ref<string | null>(null);
	const defaultFields = ref<string[]>([]);
	const blockedFields = ref<string[]>([]);

	const getVhostId = async () => {
		const urlParams = new URLSearchParams(window.location.search);  
		const queryVid = urlParams.get('vid');  
		const cookieVid = cookies.get('vid');  

		let vid = null
		if (queryVid) {
			vid = queryVid;
			cookies.set('vid', queryVid,  { maxAge: 30*86400,path:"/",secure:true });
			if(vid) {
				return vid;
			}
		}
		if (cookieVid) {
			vid = cookieVid;
			cookies.set('vid', cookieVid,  { maxAge: 30*86400,path:"/",secure:true});
			return vid;
		}


		return null;
	};
	const getVhostInfo = async (vhost: string): Promise<VhostInfo | undefined> => {
		const query = `
		query ($vhost: Int!) {
			Vhost {
				getProjectInfo(vhost: $vhost) {
					id name logo domain domainmatch class mediadetailurl emaildomain
					contactemail defaultuid moderated assetbase moderationemail
					media_deniedemail media_approvedemail storageurl storagedomain
					comments_approvedemail moderationcallback mediacallback
					sessionlifetime created streamserver streamlocation
					externalstorageid uploaderId billingPlan usehttps createdby retired
					settings
				}
			}
		}`;
		try {
			const response = await axios.post('/gql', {
				query,
				variables: { vhost: Number(vhost) },
			});
			const result = response.data?.data?.Vhost?.getProjectInfo;
			if (result && result.id) {
				result.id = String(result.id);
				return result as VhostInfo;
			}
			console.warn('getVhostInfo: no valid result for vhost', vhost);
			return undefined;
		} catch (error) {
			console.error('Error fetching vhost info:', error);
			return undefined;
		}
	};
interface ContentSummaryParams {
  vhostId: number;
  startDate?: number; // Unix timestamp in seconds
  endDate?: number;  // Unix timestamp in seconds
  channel?: string;
  fileType?: number;
  moderationStatus?: number;
}
interface UserSummaryParams {
  vhostId: number;
  startDate?: number; // Unix timestamp in seconds
  endDate?: number;  // Unix timestamp in seconds
  active?: number;
  gender?: string;
}
const getUserSummary = async ({ vhostId, startDate, endDate, active = -1, gender ='A'  }: UserSummaryParams) => {
	loading.value = true;
	error.value = null;

	// Set default to last 30 days if dates not provided
	const now = Math.floor(Date.now() / 1000);
	const defaultStartDate = now - (30 * 24 * 60 * 60);
	const finalStartDate = startDate ?? defaultStartDate;
	const finalEndDate = endDate ?? now;

	const query = `
	query GetUserSummary( $vhost: Int!  $startDate: BigInt!  $endDate: BigInt!  $active: Int,  $gender: String) {
		Vhost { getUserSummary( vhost: $vhost startDate: $startDate endDate: $endDate active: $active gender: $gender ) {
			ActiveUsers
			RegisteredUsers
			CountMale
			CountFemale
			CountUnknown
			CountOptin
			NewUsers
		}
		}
	}`;

	try {
		const response = await axios.post("/gql", {
			query,
			variables: {
				vhost: vhostId,
				startDate: finalStartDate,
				endDate: finalEndDate,
				active,
				gender
			}
		});

		const responseData = response.data?.data?.Vhost?.getUserSummary;

		if (responseData) {
			userSummary.value = responseData;
		}
		return null;
	} catch (err: any) {
		console.error("Error fetching content summary:", err);
		error.value = err.response?.data?.errors?.[0]?.message || err.message || "Failed to fetch data.";
		toast('Error', { description:  err.message });
		return null;
	} finally {
		loading.value = false;
	}
};

const getContentSummary = async ({ vhostId, startDate, endDate, channel = '', fileType = -1, moderationStatus = -1 }: ContentSummaryParams) => {
	loading.value = true;
	error.value = null;

	// Set default to last 30 days if dates not provided
	const now = Math.floor(Date.now() / 1000);
	const defaultStartDate = now - (30 * 24 * 60 * 60);
	const finalStartDate = startDate ?? defaultStartDate;
	const finalEndDate = endDate ?? now;

	const query = `
	query GetContentSummary( $vhost: Int!  $startDate: BigInt!  $endDate: BigInt!  $channel: String $fileType: Int $moderationStatus: Int) {
		Vhost { getContentSummary( vhost: $vhost startDate: $startDate endDate: $endDate channel: $channel fileType: $fileType moderationStatus: $moderationStatus) {
					FileCounts {
						Image
						Video
						Audio
						Text
						Total
					}
					Views {
						Image
						Video
						Audio
						Text
						Total
					}
					Sends {
						Image
						Video
						Audio
						Text
						Total
					}
					Comments {
						Image
						Video
						Audio
						Text
						Total
					}
					Votes
					Transfers
			}
		}
	}`;

	try {
		const response = await axios.post("/gql", {
			query,
			variables: { 
				vhost:vhostId,
				startDate: finalStartDate,
				endDate: finalEndDate,
				channel,
				fileType,
				moderationStatus
			}
		});

		const responseData = response.data?.data?.Vhost?.getContentSummary;

		if (responseData) {
			summary.value = responseData;
		}
		return null;
	} catch (err: any) {
		console.error("Error fetching content summary:", err);
		error.value = err.response?.data?.errors?.[0]?.message || err.message || "Failed to fetch data.";
		toast('Error', { description:  err.message });
		return null;
	} finally {
		loading.value = false;
	}
};

	const getTotals = async (vhostID: number, type: string = 'all', period: string = 'this month') => {
		loading.value = true;
		error.value = null;

		const query = `
		query {
			            Vhost {
					     getTotals(vhost: ${vhostID}, type: "${type}", period: "${period}") {
				totalContributorsUnderLimit
				contributorLimit
				totalContributors
				adminLimit
				totalAdminUsers
				totalFilesize
				transferCount
				totalAISeconds
				aiMinuteLimit
				aiSecondLimit
				totalAISecondsUnderLimit
			}
		}
		}
		`;

		try {
			const response = await axios.post("/gql", {
				query,
				variables: { vhostID, type, period }
			});
			totals.value = response.data?.data?.Vhost?.getTotals;
		} catch (err: any) {
			console.error("Error fetching totals:", err);
			error.value = err.message || "Failed to fetch data.";
		} finally {
			loading.value = false;
		}
	}


	const getPermissionGroups = async (vhostId: number): Promise<any[]> => {
		const query = `
		query ($vhost: Int!) {
			Vhost {
				getPermissionGroups(vhost: $vhost) {
					id name permissions
				}
			}
		}`;
		try {
			const response = await axios.post("/gql", { query, variables: { vhost: vhostId } });
			return response.data?.data?.Vhost?.getPermissionGroups || [];
		} catch (err) {
			console.error("Error fetching permission groups:", err);
			return [];
		}
	};

	const updatePermissionGroup = async (vhostId: number, groupId: number, permissions: string[]): Promise<boolean> => {
		const mutation = `
		mutation ($vhost: Int!, $groupId: Int!, $permissions: [String]!) {
			VhostMutations {
				updatePermissionGroup(vhost: $vhost, groupId: $groupId, permissions: $permissions)
			}
		}`;
		try {
			const response = await axios.post("/gql", { query: mutation, variables: { vhost: vhostId, groupId, permissions } });
			return response.data?.data?.VhostMutations?.updatePermissionGroup ?? false;
		} catch (err) {
			console.error("Error updating permission group:", err);
			return false;
		}
	};

	const createPermissionGroup = async (vhostId: number, name: string, permissions: string[]): Promise<any> => {
		const mutation = `
		mutation ($vhost: Int!, $name: String!, $permissions: [String]!) {
			VhostMutations {
				createPermissionGroup(vhost: $vhost, name: $name, permissions: $permissions) {
					id name permissions
				}
			}
		}`;
		try {
			const response = await axios.post("/gql", { query: mutation, variables: { vhost: vhostId, name, permissions } });
			return response.data?.data?.VhostMutations?.createPermissionGroup;
		} catch (err) {
			console.error("Error creating permission group:", err);
			return null;
		}
	};

	const deletePermissionGroup = async (vhostId: number, groupId: number): Promise<boolean> => {
		const mutation = `
		mutation ($vhost: Int!, $groupId: Int!) {
			VhostMutations {
				deletePermissionGroup(vhost: $vhost, groupId: $groupId)
			}
		}`;
		try {
			const response = await axios.post("/gql", { query: mutation, variables: { vhost: vhostId, groupId } });
			return response.data?.data?.VhostMutations?.deletePermissionGroup ?? false;
		} catch (err) {
			console.error("Error deleting permission group:", err);
			return false;
		}
	};

	const getTabs = async (vhostId: number): Promise<any[]> => {
		const query = `
		query ($vhost: Int!) {
			Vhost {
				getTabs(vhost: $vhost) {
					id name label icon sort
				}
			}
		}`;
		try {
			const response = await axios.post("/gql", { query, variables: { vhost: vhostId } });
			return response.data?.data?.Vhost?.getTabs || [];
		} catch (err) {
			console.error("Error fetching tabs:", err);
			return [];
		}
	};

	const getCustomMeta = async (vhostId: number, mediaID: number = 0): Promise<any[]> => {
		const query = `
		query ($vhost: Int!, $mediaID: Int) {
			Vhost {
				getCustomMeta(vhost: $vhost, mediaID: $mediaID) {
					id key name label type grouping required options defaultValue prefill sort source provider maxsize settings
				}
			}
		}`;
		try {
			const response = await axios.post("/gql", { query, variables: { vhost: vhostId, mediaID } });
			return response.data?.data?.Vhost?.getCustomMeta || [];
		} catch (err) {
			console.error("Error fetching custom meta:", err);
			return [];
		}
	};

	const getCustomMetaProviders = async (vhostId: number): Promise<any[]> => {
		const query = `
		query ($vhost: Int!) {
			Vhost {
				getCustomMetaProviders(vhost: $vhost) {
					id vhost provider sortable_rank
				}
			}
		}`;
		try {
			const response = await axios.post("/gql", { query, variables: { vhost: vhostId } });
			return response.data?.data?.Vhost?.getCustomMetaProviders || [];
		} catch (err) {
			console.error("Error fetching custom meta providers:", err);
			return [];
		}
	};

	const getCustomMetaGroupings = async (vhostId: number): Promise<any[]> => {
		const query = `
		query ($vhost: Int!) {
			Vhost {
				getCustomMetaGroupings(vhost: $vhost) {
					id name sort
				}
			}
		}`;
		try {
			const response = await axios.post("/gql", { query, variables: { vhost: vhostId } });
			return response.data?.data?.Vhost?.getCustomMetaGroupings || [];
		} catch (err) {
			console.error("Error fetching custom meta groupings:", err);
			return [];
		}
	};

	const search3rdPartyMeta = async (vhostId: number, query: string, parentId: string = ''): Promise<any[]> => {
		const gqlQuery = `
		query ($vhost: Int!, $query: String, $parentId: String) {
			Vhost {
				search3rdPartyMeta(vhost: $vhost, query: $query, parentId: $parentId)
			}
		}`;
		try {
			const response = await axios.post("/gql", { query: gqlQuery, variables: { vhost: vhostId, query, parentId } });
			let result = response.data?.data?.Vhost?.search3rdPartyMeta || [];
			// GQL JSON scalar may return a JSON-encoded string — parse it
			if (typeof result === 'string') {
				try { result = JSON.parse(result); } catch { result = []; }
			}
			return Array.isArray(result) ? result : [];
		} catch (err) {
			console.error("Error searching 3rd party meta:", err);
			return [];
		}
	};

	const generateValue = async (vhostId: number, metaID: number, data: any[]): Promise<{ key: string; value: string } | null> => {
		const gqlQuery = `
		query ($vhost: Int!, $metaID: Int!, $data: JSON) {
			Vhost {
				generateValue(vhost: $vhost, metaID: $metaID, data: $data)
			}
		}`;
		try {
			console.log('[generateValue] Calling GQL with vhost:', vhostId, 'metaID:', metaID, 'data:', data);
			const response = await axios.post("/gql", { query: gqlQuery, variables: { vhost: vhostId, metaID, data: JSON.stringify(data) } });
			console.log('[generateValue] Raw response:', JSON.stringify(response.data));
			let result = response.data?.data?.Vhost?.generateValue;
			console.log('[generateValue] Extracted result (before parse):', result, 'type:', typeof result);
			if (typeof result === 'string') {
				try { result = JSON.parse(result); } catch { console.log('[generateValue] Failed to parse result string'); return null; }
			}
			console.log('[generateValue] Final result:', result);
			return result || null;
		} catch (err) {
			console.error("[generateValue] Error:", err);
			return null;
		}
	};

	const updateProjectSettings = async (vhostId: number, settings: Record<string, any>): Promise<boolean> => {
		const mutation = `
		mutation ($vhost: Int!, $settings: String!) {
			VhostMutations {
				updateProjectSettings(vhost: $vhost, settings: $settings)
			}
		}`;
		try {
			const response = await axios.post("/gql", {
				query: mutation,
				variables: { vhost: vhostId, settings: JSON.stringify(settings) }
			});
			return response.data?.data?.VhostMutations?.updateProjectSettings ?? false;
		} catch (err) {
			console.error("Error updating project settings:", err);
			return false;
		}
	};

	return {
		getVhostId, getVhostInfo, getTotals, totals, loading, error,
		userSummary, getUserSummary, summary, getContentSummary,
		getPermissionGroups, updatePermissionGroup, createPermissionGroup, deletePermissionGroup,
		getTabs, getCustomMeta, getCustomMetaProviders, getCustomMetaGroupings,
		updateProjectSettings, search3rdPartyMeta, generateValue,
	};
}
